/**
 * CounterOfferDialog
 * Dialog for submitting a counter-offer (used by both client and contractor/verifier).
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Close,
  Send,
  CheckCircle,
  SwapHoriz,
  AttachMoney,
  CalendarToday,
  Schedule,
  Warning
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand, bg, border } from '../../theme/colors';

const CounterOfferDialog = ({ open, onClose, quoteRequest, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [currency] = useState('USD');
  const [proposedStartDate, setProposedStartDate] = useState('');
  const [estimatedTimelineDays, setEstimatedTimelineDays] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const currentRound = quoteRequest?.negotiation_round || 0;
  const maxRounds = quoteRequest?.max_negotiation_rounds || 5;
  const nextRound = currentRound + 1;
  const isLastRound = nextRound >= maxRounds;

  const handleSubmit = async () => {
    // $0 is a legitimate counter — e.g. countering a price that was really
    // just a site-visit charge (site visits are free on Eqaya).
    if (amount === '' || Number.isNaN(parseFloat(amount)) || parseFloat(amount) < 0) {
      setError('Please enter a valid counter-offer amount ($0 or more)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/quote-requests/${quoteRequest.id}/counter-offer`, {
        amount: parseFloat(amount),
        currency,
        proposedStartDate: proposedStartDate || null,
        estimatedTimelineDays: estimatedTimelineDays ? parseInt(estimatedTimelineDays) : null,
        message: message || null
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data.data);
          handleClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to send counter-offer');
      }
    } catch (err) {
      console.error('Counter-offer error:', err);
      setError(err.response?.data?.message || 'Failed to send counter-offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAmount('');
      setProposedStartDate('');
      setEstimatedTimelineDays('');
      setMessage('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!quoteRequest) return null;

  const currentAmount = quoteRequest.quote_amount;
  const currentCurrency = quoteRequest.quote_currency || 'USD';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          py: 3,
          px: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <SwapHoriz sx={{ mr: 1, verticalAlign: 'middle' }} />
              Make Counter-Offer
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              Round {nextRound} of {maxRounds}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {success && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Counter-Offer Sent!</Typography>
            <Typography variant="body2" color="text.secondary">
              The other party will be notified.
            </Typography>
          </Box>
        )}

        {error && !success && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!success && (
          <>
            {/* Last round warning */}
            {isLastRound && (
              <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
                This is the <strong>last negotiation round</strong>. After this, the quote must be accepted or declined.
              </Alert>
            )}

            {/* Current Offer */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: bg.subtle,
                border: `1px solid ${border.default}`,
                borderRadius: 2
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Current Offer
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: brand.primary }}>
                {currentAmount
                  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currentCurrency }).format(currentAmount)
                  : 'No amount yet'
                }
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                {quoteRequest.proposed_start_date && (
                  <Typography variant="body2" color="text.secondary">
                    Start: {new Date(quoteRequest.proposed_start_date).toLocaleDateString()}
                  </Typography>
                )}
                {quoteRequest.estimated_timeline_days && (
                  <Typography variant="body2" color="text.secondary">
                    {quoteRequest.estimated_timeline_days} days
                  </Typography>
                )}
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Your Counter-Offer
            </Typography>

            {/* Counter Amount */}
            <TextField
              fullWidth
              label="Counter-Offer Amount (USD) *"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              placeholder="Enter your counter-offer price"
              helperText="You can offer $0 — for example if the quoted amount was really a site-visit charge. Site visits are always free on Eqaya."
              sx={{ mb: 2 }}
            />

            {/* Start Date & Timeline */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Proposed Start Date"
                type="date"
                value={proposedStartDate}
                onChange={(e) => setProposedStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" /></InputAdornment>
                }}
              />
              <TextField
                fullWidth
                label="Timeline (Days)"
                type="number"
                value={estimatedTimelineDays}
                onChange={(e) => setEstimatedTimelineDays(e.target.value)}
                placeholder="e.g., 30"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Schedule fontSize="small" /></InputAdornment>
                }}
              />
            </Box>

            {/* Message */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Explain your counter-offer..."
            />
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}` }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || amount === ''}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              fontWeight: 600,
              px: 3
            }}
          >
            Send Counter-Offer
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CounterOfferDialog;
